import React from 'react'
import Layout from '../components/Layout.js'
import { SUPPORT_EMAIL, PRIVACY_POLICY_EFFECTIVE_DATE } from '../config.js'

const Privacy = () => {
  const sectionHeading = (text) => (
    <h2 className="mt-12 mb-4 text-lg tracking-wider text-gray-600 dark:text-gray-500 md:text-xl lg:text-2xl">
      {text}
    </h2>
  );

  const paragraph = (children) => (
    <p className="mb-4 text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
      {children}
    </p>
  );

  const list = (items) => (
    <ul className="mb-4 ml-5 list-disc text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
      {items.map((item, index) => <li key={index}>{item}</li>)}
    </ul>
  );

  const link = (href, text) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-purple-600 dark:text-purple-400 hover:text-pink-600 dark:hover:text-pink-400 transition-colors duration-300 underline decoration-purple-600/30 dark:decoration-purple-400/30"
    >
      {text}
    </a>
  );

  return (
    <Layout title="Privacy Policy" marginBottomClass={"mb-0"}>
      <section className="font-mono max-w-4xl mx-auto xl:px-12 pb-24">
        <h1 className="text-gray-900 dark:text-white mt-[50px] mb-2 text-[40px] tracking-wide max-md:text-[28px] max-md:mt-[30px]">
          <span className="text-gradient from-purple-600 to-pink-600 pl-1.5 pr-1.5">
            Privacy Policy
          </span>
        </h1>
        <p className="mb-8 text-[12px] lg:text-[14px] text-gray-600 dark:text-gray-500 tracking-wider">
          Effective {PRIVACY_POLICY_EFFECTIVE_DATE}
        </p>

        {paragraph(<>
          Effem is a podcast player for iOS made by SparrowTek LLC ("we", "us"). This policy explains what
          information the Effem app, the Effem services we operate, and this website handle, and the choices
          you have. The short version: your listening data lives on your device, the social features are built
          on your own AT Protocol account, and we do not sell data or run advertising.
        </>)}

        {sectionHeading('Information the app stores on your device')}
        {paragraph(<>
          Your subscriptions, listening history, playback positions, playlists, downloaded episodes, and
          settings are stored locally on your device. Effem does not upload this information anywhere unless
          you sign in with an AT Protocol account and use the sync and social features described below. You can
          delete downloaded episodes at any time from Settings, and deleting the app removes all local data.
        </>)}

        {sectionHeading('Podcast content and third-party hosts')}
        {paragraph(<>
          Podcast audio, artwork, and chapter files are fetched directly from the servers of the podcast's
          publisher or hosting provider. Those servers receive the standard information any web request carries,
          such as your IP address and the app's user agent, and are governed by their own privacy policies.
          Effem does not add tracking identifiers to these requests.
        </>)}

        {sectionHeading('Search and podcast directory')}
        {paragraph(<>
          Search, trending, and podcast metadata are provided through the Effem AppView (appview.effem.xyz), a
          service we operate that queries the {link('https://podcastindex.org', 'Podcast Index')}, an open podcast
          directory. Your search terms are sent to our AppView and on to the Podcast Index to return results.
          They are not linked to your identity and are not used to build a profile of you.
        </>)}

        {sectionHeading('Signing in with an AT Protocol account (optional)')}
        {paragraph(<>
          You can use Effem without an account. If you choose to sign in, Effem uses AT Protocol OAuth: you
          enter your password on your own Personal Data Server's (PDS) sign-in page, never in Effem, and Effem
          never sees or stores your password. Effem stores the resulting access tokens in the device Keychain
          and uses an authentication service we operate (auth.effem.xyz) to complete the sign-in and token
          refresh handshake with your PDS. Signing out removes the tokens from your device.
        </>)}
        {paragraph(<>
          When you are signed in, Effem stores your account identifier (your DID and handle) on the device so it
          can attribute your activity. This is the only account information Effem collects.
        </>)}

        {sectionHeading('Data written to your AT Protocol repository')}
        {paragraph(<>
          The AT Protocol is a public, decentralized network. When you use the following features, Effem writes
          records to your own repository on your PDS, under your control:
        </>)}
        {list([
          'Podcast subscriptions, so your library can sync between devices.',
          'Episode state, such as playback position and whether an episode was played.',
          'Comments you post on episodes, replies, and likes.',
          'Users you block, and reports you submit about content.',
        ])}
        {paragraph(<>
          These records are public by design of the protocol and may be read by any AT Protocol service,
          including other podcast apps. Unsubscribing, deleting a comment, or unblocking a user removes the
          corresponding record. You can also manage or delete records directly through your PDS provider.
        </>)}

        {sectionHeading('The Effem AppView')}
        {paragraph(<>
          To power social features, our AppView indexes public Effem records from the AT Protocol network,
          including yours if you use the features above, and serves them back to the app (for example, the
          comments on an episode or how many people subscribe to a show). When a record is deleted from your
          repository, the AppView removes it from its index as the deletion propagates through the network.
          Blocking a user hides their content from you inside Effem.
        </>)}
        {paragraph(<>
          Like any web service, the AppView and authentication service keep short-lived server logs containing
          your IP address and the request path for security, rate limiting, and abuse prevention. Query strings
          are not logged. These logs are not used for analytics or advertising.
        </>)}

        {sectionHeading('Notifications')}
        {paragraph(<>
          New-episode and download-complete notifications are scheduled locally on your device. Effem does not
          send push notifications through our servers and does not upload a device push token.
        </>)}

        {sectionHeading('Analytics and tracking')}
        {paragraph(<>
          The Effem app contains no analytics frameworks, advertising SDKs, or third-party tracking. We rely on
          Apple's opt-in crash and diagnostic reporting only.
        </>)}
        {paragraph(<>
          This website (effem.xyz) uses Google Tag Manager, which may load Google Analytics to measure page
          views. It is not loaded on the OAuth callback page used during sign-in. You can limit this with your
          browser's tracking protection or an ad blocker.
        </>)}

        {sectionHeading('Children')}
        {paragraph(<>
          Effem is not directed to children under 13, and we do not knowingly collect personal information from
          them.
        </>)}

        {sectionHeading('Your choices')}
        {list([
          'Use Effem without signing in; all features except sync and social interaction work offline-first.',
          'Sign out at any time to remove tokens and your account identifier from the device.',
          'Delete your subscription, episode-state, comment, and block records from the app or from your PDS.',
          'Delete downloaded episodes from Settings, or delete the app to remove everything stored locally.',
        ])}

        {sectionHeading('Changes to this policy')}
        {paragraph(<>
          We will post any changes on this page and update the effective date above. Material changes will also
          be noted in the app's release notes.
        </>)}

        {sectionHeading('Contact')}
        {paragraph(<>
          Questions about this policy or your data: {link(`mailto:${SUPPORT_EMAIL}`, SUPPORT_EMAIL)}.
        </>)}
      </section>
    </Layout>
  );
}

export default Privacy
